/**
 * Vue Router Configuration
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { authGuard, setPageTitle } from './guards'

const routes: RouteRecordRaw[] = [
  // Auth routes (guest only)
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresGuest: true, title: 'Login' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { requiresGuest: true, title: 'Register' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresGuest: true, title: 'Forgot Password' },
  },
  // Public Raise Room walkthrough. TOP-LEVEL and outside MainLayout so it has
  // no `requiresAuth` in its matched chain — an unauthenticated visitor reaches
  // it directly. It renders from a hardcoded fixture and makes no API calls, so
  // there is no path from this page to the database or to any tenant.
  {
    path: '/demo/raise-room',
    name: 'demo-raise-room',
    component: () => import('@/views/demo/DemoRaiseRoomView.vue'),
    meta: { title: 'The Raise Room — demo', public: true },
  },
  {
    path: '/accept-invitation',
    name: 'accept-invitation',
    component: () => import('@/views/auth/AcceptInvitationView.vue'),
    meta: { requiresGuest: false, title: 'Accept Invitation', public: true },
  },
  // Client portal settings — only meaningful for ClientUsers. Behind auth.
  {
    path: '/client/settings',
    name: 'client-settings',
    component: () => import('@/views/client/ClientSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Settings' },
  },

  // App routes (authenticated)
  {
    path: '/',
    component: () => import('@/components/layout/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // Home — the new calm "Today" is the landing page (reframe). The old
      // Command Center (DashboardView) is superseded; Strategy holds the
      // dense dashboards now.
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/today/TodayView.vue'),
        meta: { title: 'Today' },
      },

      // Clients Overview (Agency Staff Only)
      {
        path: 'clients',
        name: 'clients-overview',
        component: () => import('@/views/clients/ClientsOverviewView.vue'),
        meta: { title: 'Clients', roles: ['admin', 'partner', 'senior_consultant'] },
      },

      // Import Data (accessible to both team users and client portal users)
      // Backend enforces tenant scoping — client users can only import into their own tenant.
      {
        path: 'import',
        name: 'import-data',
        component: () => import('@/views/settings/ImportDataView.vue'),
        meta: { title: 'Import Data' },
      },

      // 360° View (CDP Profiles Hub)
      {
        path: 'profiles',
        name: 'profiles-hub',
        component: () => import('@/views/profiles/ProfilesHubView.vue'),
        meta: { title: '360° View' },
      },
      {
        path: 'segments',
        name: 'segments',
        component: () => import('@/views/profiles/SegmentBuilderView.vue'),
        meta: { title: 'Segments' },
      },

      // Contacts
      {
        path: 'contacts',
        name: 'contacts',
        component: () => import('@/views/contacts/ContactsView.vue'),
        meta: { title: 'Contacts' },
      },
      {
        path: 'contacts/:id',
        name: 'contact-detail',
        component: () => import('@/views/contacts/ContactDetailView.vue'),
        meta: { title: 'Contact Details' },
      },

      // Companies
      {
        path: 'companies',
        name: 'companies',
        component: () => import('@/views/companies/CompaniesView.vue'),
        meta: { title: 'Companies' },
      },
      {
        path: 'companies/:id',
        name: 'company-detail',
        component: () => import('@/views/companies/CompanyDetailView.vue'),
        meta: { title: 'Company Details' },
      },
      {
        path: 'companies/:id/integrations',
        name: 'company-integrations',
        component: () => import('@/views/companies/CompanyIntegrationsView.vue'),
        meta: { title: 'Company Integrations' },
      },
      {
        path: 'companies/:id/setup',
        name: 'client-setup',
        component: () => import('@/views/companies/ClientSetupWizard.vue'),
        meta: { title: 'Client Setup' },
      },

      // Deals / Pipeline
      {
        path: 'deals',
        name: 'deals',
        component: () => import('@/views/deals/DealsView.vue'),
        meta: { title: 'Sales Pipeline' },
      },
      {
        path: 'deals/:id',
        name: 'deal-detail',
        component: () => import('@/views/deals/DealDetailView.vue'),
        meta: { title: 'Deal Details' },
      },

      // Contracts
      {
        path: 'contracts',
        name: 'contracts',
        component: () => import('@/views/contracts/ContractsView.vue'),
        meta: { title: 'Contracts' },
      },
      {
        path: 'contracts/msa/:id',
        name: 'msa-detail',
        component: () => import('@/views/contracts/MSADetailView.vue'),
        meta: { title: 'MSA Details' },
      },
      {
        path: 'contracts/sow/:id',
        name: 'sow-detail',
        component: () => import('@/views/contracts/SOWDetailView.vue'),
        meta: { title: 'SOW Details' },
      },
      {
        path: 'contracts/sa/:id',
        name: 'sa-detail',
        component: () => import('@/views/contracts/ServiceAgreementDetailView.vue'),
        meta: { title: 'Service Agreement Details' },
      },

      // Meeting Scheduler
      {
        path: 'meetings',
        name: 'meetings',
        component: () => import('@/views/meetings/MeetingSchedulerView.vue'),
        meta: { title: 'Meeting Scheduler' },
      },

      // Coworking Spaces (bookable-resource scheduling prototype)
      {
        path: 'spaces',
        name: 'front-desk',
        component: () => import('@/views/spaces/FrontDeskView.vue'),
        meta: { title: 'Front Desk' },
      },
      {
        path: 'spaces/manage',
        name: 'spaces-manage',
        component: () => import('@/views/spaces/SpacesView.vue'),
        meta: { title: 'Manage Spaces' },
      },
      {
        path: 'spaces/book',
        name: 'book-space',
        component: () => import('@/views/spaces/BookSpaceView.vue'),
        meta: { title: 'Book a Space' },
      },
      {
        path: 'spaces/members',
        name: 'space-members',
        component: () => import('@/views/spaces/MembersView.vue'),
        meta: { title: 'Members' },
      },
      {
        path: 'spaces/calendar',
        name: 'space-calendar',
        component: () => import('@/views/spaces/SpaceCalendarView.vue'),
        meta: { title: 'Calendar' },
      },
      {
        path: 'spaces/metrics',
        name: 'space-metrics',
        component: () => import('@/views/spaces/SpaceMetricsView.vue'),
        meta: { title: 'Metrics' },
      },
      {
        // The events bible (Events tab). Inquiries now land in the Leads inbox;
        // the legacy standalone inquiry list moved under it.
        path: 'spaces/events',
        name: 'space-events',
        component: () => import('@/views/spaces/EventsView.vue'),
        meta: { title: 'Events' },
      },
      {
        path: 'spaces/events/inquiries',
        name: 'space-event-inquiries',
        component: () => import('@/views/spaces/EventInquiriesView.vue'),
        meta: { title: 'Event inquiries (legacy)' },
      },
      {
        path: 'spaces/events/:id',
        name: 'space-event-detail',
        component: () => import('@/views/spaces/EventDetailView.vue'),
        meta: { title: 'Event' },
      },
      {
        path: 'spaces/leads',
        name: 'space-leads',
        component: () => import('@/views/spaces/IntakeInboxView.vue'),
        meta: { title: 'Lead inbox' },
      },
      {
        path: 'spaces/leads/board',
        name: 'space-leads-board',
        component: () => import('@/views/spaces/LeadBoardView.vue'),
        meta: { title: 'Lead board' },
      },
      {
        path: 'spaces/leads/analytics',
        name: 'space-leads-analytics',
        component: () => import('@/views/spaces/LeadAnalyticsView.vue'),
        meta: { title: 'Lead funnel' },
      },
      {
        path: 'spaces/members/lifecycle',
        name: 'space-member-lifecycle',
        component: () => import('@/views/spaces/MemberLifecycleView.vue'),
        meta: { title: 'Membership lifecycle' },
      },
      // Distribution (direct store delivery) — Creative Crusts Pizza. See views/distribution.
      {
        path: 'dist',
        name: 'dist-dashboard',
        component: () => import('@/views/distribution/DistDashboardView.vue'),
        meta: { title: 'Shelf performance' },
      },
      {
        path: 'dist/log',
        name: 'dist-log',
        component: () => import('@/views/distribution/DistLogView.vue'),
        meta: { title: 'Log a visit' },
      },
      {
        path: 'dist/route',
        name: 'dist-route',
        component: () => import('@/views/distribution/DistRouteView.vue'),
        meta: { title: 'Route' },
      },
      {
        path: 'dist/stores',
        name: 'dist-stores',
        component: () => import('@/views/distribution/DistStoresView.vue'),
        meta: { title: 'Stores' },
      },
      {
        path: 'dist/marketing',
        name: 'dist-marketing',
        component: () => import('@/views/distribution/DistMarketingView.vue'),
        meta: { title: 'Marketing' },
      },
      {
        path: 'dist/flavors',
        name: 'dist-flavors',
        component: () => import('@/views/distribution/DistFlavorsView.vue'),
        meta: { title: 'Flavors & pricing' },
      },
      {
        path: 'dist/costs',
        name: 'dist-costs',
        component: () => import('@/views/distribution/DistCostsView.vue'),
        meta: { title: 'Costs' },
      },
      {
        path: 'dist/import',
        name: 'dist-import',
        component: () => import('@/views/distribution/DistImportView.vue'),
        meta: { title: 'Import history' },
      },
      {
        path: 'spaces/announcements',
        name: 'space-announcements',
        component: () => import('@/views/spaces/AnnouncementsView.vue'),
        meta: { title: 'Announcements' },
      },
      {
        path: 'spaces/documents',
        name: 'space-documents',
        component: () => import('@/views/spaces/CoworkingDocumentsView.vue'),
        meta: { title: 'Documents' },
      },
      {
        path: 'spaces/leads/types',
        name: 'space-intake-types',
        component: () => import('@/views/spaces/IntakeTypesView.vue'),
        meta: { title: 'Intake types' },
      },
      {
        path: 'website',
        name: 'site-builder',
        component: () => import('@/views/site/SiteBuilderView.vue'),
        meta: { title: 'Website' },
      },

      // QuickBooks
      {
        path: 'quickbooks',
        name: 'quickbooks',
        component: () => import('@/views/quickbooks/QuickBooksView.vue'),
        meta: { title: 'QuickBooks' },
      },

      // Projects
      {
        path: 'projects',
        name: 'projects',
        component: () => import('@/views/projects/ProjectsView.vue'),
        meta: { title: 'Projects' },
      },
      {
        path: 'projects/:id',
        name: 'project-detail',
        component: () => import('@/views/projects/ProjectDetailView.vue'),
        meta: { title: 'Project Details' },
      },

      // Time Tracking
      {
        path: 'time-tracking',
        name: 'time-tracking',
        component: () => import('@/views/time-tracking/TimeTrackingView.vue'),
        meta: { title: 'Time Tracking' },
      },
      {
        path: 'time-tracking/timesheet',
        name: 'timesheet',
        component: () => import('@/views/time-tracking/TimesheetView.vue'),
        meta: { title: 'Timesheet' },
      },
      {
        path: 'time-tracking/daily-review',
        name: 'daily-review',
        component: () => import('@/views/time-tracking/DailyReviewView.vue'),
        meta: { title: 'Daily Review' },
      },

      // Proposals
      {
        path: 'proposals',
        name: 'proposals',
        component: () => import('@/views/proposals/ProposalsView.vue'),
        meta: { title: 'Proposals' },
      },
      {
        path: 'proposals/new',
        name: 'proposal-new',
        component: () => import('@/views/proposals/ProposalEditView.vue'),
        meta: { title: 'New Proposal' },
      },
      {
        path: 'proposals/:id',
        name: 'proposal-detail',
        component: () => import('@/views/proposals/ProposalDetailView.vue'),
        meta: { title: 'Proposal Details' },
      },
      {
        path: 'proposals/:id/edit',
        name: 'proposal-edit',
        component: () => import('@/views/proposals/ProposalEditView.vue'),
        meta: { title: 'Edit Proposal' },
      },

      // Invoices
      {
        path: 'invoices',
        name: 'invoices',
        component: () => import('@/views/invoices/InvoicesView.vue'),
        meta: { title: 'Invoices' },
      },
      {
        path: 'invoices/new',
        name: 'invoice-new',
        component: () => import('@/views/invoices/InvoiceEditView.vue'),
        meta: { title: 'New Invoice' },
      },
      {
        path: 'invoices/:id',
        name: 'invoice-detail',
        component: () => import('@/views/invoices/InvoiceDetailView.vue'),
        meta: { title: 'Invoice Details' },
      },
      {
        path: 'invoices/:id/edit',
        name: 'invoice-edit',
        component: () => import('@/views/invoices/InvoiceEditView.vue'),
        meta: { title: 'Edit Invoice' },
      },

      // Activity Feed
      {
        path: 'activities',
        name: 'activities',
        component: () => import('@/views/activities/ActivityFeedView.vue'),
        meta: { title: 'Activity Feed' },
      },

      // Microsoft 365 (Outlook, Calendar, OneDrive)
      {
        path: 'email',
        name: 'email-inbox',
        component: () => import('@/views/microsoft/EmailInboxView.vue'),
        meta: { title: 'Email' },
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('@/views/microsoft/CalendarView.vue'),
        meta: { title: 'Calendar' },
      },
      {
        path: 'files',
        name: 'files',
        component: () => import('@/views/microsoft/FilesView.vue'),
        meta: { title: 'Files' },
      },
      {
        path: 'planner',
        name: 'planner',
        component: () => import('@/views/microsoft/PlannerView.vue'),
        meta: { title: 'Planner' },
      },
      {
        path: 'bookings',
        name: 'bookings',
        component: () => import('@/views/microsoft/BookingsView.vue'),
        meta: { title: 'Bookings' },
      },
      {
        path: 'tasks',
        name: 'unified-tasks',
        component: () => import('@/views/tasks/UnifiedTasksView.vue'),
        meta: { title: 'Tasks' },
      },
      {
        path: 'tasks/todo',
        name: 'microsoft-tasks',
        component: () => import('@/views/microsoft/MicrosoftTasksView.vue'),
        meta: { title: 'To-Do Lists' },
      },

      // Social Media
      {
        path: 'social',
        name: 'social',
        component: () => import('@/views/social/SocialDashboardView.vue'),
        meta: { title: 'Social Media' },
      },
      {
        path: 'social/compose',
        name: 'social-compose',
        component: () => import('@/views/social/SocialComposeView.vue'),
        meta: { title: 'Create Post' },
      },
      {
        path: 'social/posts',
        name: 'social-posts',
        component: () => import('@/views/social/SocialPostsView.vue'),
        meta: { title: 'Social Posts' },
      },
      {
        path: 'social/calendar',
        name: 'social-calendar',
        component: () => import('@/views/social/SocialCalendarView.vue'),
        meta: { title: 'Content Calendar' },
      },
      {
        path: 'social/analytics',
        name: 'social-analytics',
        component: () => import('@/views/social/SocialAnalyticsView.vue'),
        meta: { title: 'Social Analytics' },
      },
      {
        path: 'social/queue',
        name: 'social-queue',
        component: () => import('@/views/social/SocialQueueView.vue'),
        meta: { title: 'Post Queue' },
      },

      // Intelligence / Insights hub
      // 2026-06-22: /intelligence now loads the menu-driven InsightsHubView
      // (HubGrid + RBAC + integration gating from menu.ts). The older
      // IntelligenceDashboardView (CDP engagement scoring / multi-touch
      // attribution dashboard) is kept available at /intelligence/dashboard
      // for any deep links.
      {
        path: 'intelligence',
        name: 'intelligence',
        component: () => import('@/views/insights/InsightsHubView.vue'),
        meta: { title: 'Insights' },
      },
      {
        path: 'intelligence/dashboard',
        name: 'intelligence-dashboard',
        component: () => import('@/views/intelligence/IntelligenceDashboardView.vue'),
        meta: { title: 'Intelligence Dashboard' },
      },
      {
        path: 'intelligence/ad-platforms',
        name: 'ad-platforms',
        component: () => import('@/views/intelligence/AdPlatformsView.vue'),
        meta: { title: 'Ad Platforms' },
      },
      {
        path: 'intelligence/predictive',
        name: 'predictive-insights',
        component: () => import('@/views/intelligence/PredictiveInsightsView.vue'),
        meta: { title: 'Predictive Insights' },
      },
      {
        path: 'journeys',
        name: 'journeys',
        component: () => import('@/views/intelligence/JourneyBuilderView.vue'),
        meta: { title: 'Journey Builder' },
      },
      {
        path: 'journeys/:id',
        name: 'journey-detail',
        component: () => import('@/views/intelligence/JourneyBuilderView.vue'),
        meta: { title: 'Journey Builder' },
      },
      {
        path: 'intelligence/leads',
        name: 'lead-database',
        component: () => import('@/views/intelligence/LeadSearchView.vue'),
        meta: { title: 'Lead Database' },
      },

      // Role-based KPI dashboard — PowerBI-style tile grid.
      // Desktop-dense layout for work-at-desk use; mobile re-flow for
      // phones. Companion to the narrative /today briefing (which is
      // read-with-coffee mode).
      {
        path: 'dashboard',
        name: 'exec-dashboard',
        component: () => import('@/views/dashboard/ExecDashboardView.vue'),
        meta: { title: 'Dashboard' },
      },

      // Design system — living style reference for the "bright structure" look.
      {
        path: 'design',
        name: 'design-system',
        component: () => import('@/views/design/DesignSystemView.vue'),
        meta: { title: 'Design System' },
      },

      // Ask — conversational analytics over the tenant's data (GTM hero feature).
      {
        path: 'ask',
        name: 'ask',
        component: () => import('@/views/ask/AskView.vue'),
        meta: { title: 'Ask' },
      },

      // ── 5-space reframe: top-level spaces ──
      // Strategy — predictive/analytical space (tabs over existing views).
      {
        path: 'strategy',
        name: 'strategy',
        component: () => import('@/views/strategy/StrategyHubView.vue'),
        meta: { title: 'Strategy' },
      },
      // Records — one home for all business data (entity switcher hub).
      {
        path: 'records',
        name: 'records',
        component: () => import('@/views/records/RecordsHubView.vue'),
        meta: { title: 'Records' },
      },
      // Outreach — channels hub (sequences, campaigns, social, forms).
      {
        path: 'outreach',
        name: 'outreach',
        component: () => import('@/views/outreach/OutreachHubView.vue'),
        meta: { title: 'Outreach' },
      },
      // AI outreach: sequence builder, the review queue (Outbox), and Replies.
      {
        path: 'outreach/builder',
        name: 'outreach-builder',
        component: () => import('@/views/outreach/AiSequenceBuilderView.vue'),
        meta: { title: 'AI sequence builder' },
      },
      {
        path: 'outreach/outbox',
        name: 'outreach-outbox',
        component: () => import('@/views/outreach/OutboxView.vue'),
        meta: { title: 'Outbox' },
      },
      // The 50% Question — outside-in prospect briefs (staff only).
      {
        path: 'outreach/briefs',
        name: 'outreach-briefs',
        component: () => import('@/views/outreach/BriefsView.vue'),
        meta: { title: 'The 50% Question' },
      },
      {
        path: 'outreach/briefs/:id',
        name: 'outreach-brief-builder',
        component: () => import('@/views/outreach/BriefBuilderView.vue'),
        meta: { title: 'Brief builder' },
      },
      {
        path: 'outreach/replies',
        name: 'outreach-replies',
        component: () => import('@/views/outreach/RepliesView.vue'),
        meta: { title: 'Replies' },
      },
      // Intelligence — recommendations + knowledge + CDP depth (audiences).
      // Note: /insights is the standing recommendations feed; this hub lives at
      // /intelligence and links out to it plus playbooks, profiles, segments…
      {
        path: 'intelligence',
        name: 'intelligence-hub',
        component: () => import('@/views/insights/InsightsHubView.vue'),
        meta: { title: 'Intelligence' },
      },
      // Decisions — the decision-stewardship trust loop (Method-framed
      // recommendations decided on the record + the decision log).
      {
        path: 'decisions',
        name: 'decisions',
        component: () => import('@/views/insights/DecisionsView.vue'),
        meta: { title: 'Decisions' },
      },
      // Ventures — startup evaluation and stewardship. Normalizes a client's
      // financial spreadsheet into the canonical Mavriq 3-statement model, runs
      // the evaluation interview, and drafts the plan and pitch off that
      // evidence. Ends in a Method-framed Decision on /decisions.
      {
        path: 'ventures',
        name: 'ventures',
        component: () => import('@/views/ventures/VenturesHubView.vue'),
        meta: { title: 'Ventures' },
      },
      {
        path: 'ventures/:id',
        name: 'venture-detail',
        component: () => import('@/views/ventures/VentureDetailView.vue'),
        meta: { title: 'Venture' },
      },
      {
        path: 'ventures/:id/financials',
        name: 'venture-financials',
        component: () => import('@/views/ventures/VentureFinancialsView.vue'),
        meta: { title: 'Venture financials' },
      },
      {
        path: 'ventures/:id/interview',
        name: 'venture-interview',
        component: () => import('@/views/ventures/VentureInterviewView.vue'),
        meta: { title: 'Venture evaluation' },
      },
      {
        path: 'ventures/:id/documents',
        name: 'venture-documents',
        component: () => import('@/views/ventures/VentureDocumentsView.vue'),
        meta: { title: 'Plan & pitch' },
      },
      {
        path: 'ventures/:id/workbook',
        name: 'venture-workbook',
        component: () => import('@/views/ventures/VentureWorkbookView.vue'),
        meta: { title: 'Venture workbook' },
      },
      // Founder network — global identity + membership graph on Postgres,
      // separate bounded context from the CRM. See docs and the architecture
      // assessment; community/chat is deliberately NOT built here.
      {
        path: 'raise-room',
        name: 'network-home',
        component: () => import('@/views/network/NetworkHomeView.vue'),
        meta: { title: 'The Raise Room' },
      },
      {
        path: 'raise-room/cohort',
        name: 'network-cohort',
        component: () => import('@/views/network/CohortBoardView.vue'),
        meta: { title: 'Cohort board' },
      },
      {
        path: 'raise-room/o/:orgId',
        name: 'network-founder-dashboard',
        component: () => import('@/views/network/FounderDashboardView.vue'),
        meta: { title: 'Founder dashboard' },
      },
      {
        path: 'raise-room/o/:orgId/investor-view',
        name: 'network-investor-view',
        component: () => import('@/views/network/InvestorVentureView.vue'),
        meta: { title: 'Company profile' },
      },
      // Delivery Planner — capability-based engagement scoping (Postgres module,
      // agency-internal). See docs/DELIVERY_PLANNER_DESIGN.md.
      {
        path: 'delivery',
        name: 'delivery-hub',
        component: () => import('@/views/delivery/DeliveryHubView.vue'),
        meta: { title: 'Delivery Planner' },
      },
      {
        path: 'delivery/:id',
        name: 'delivery-engagement',
        component: () => import('@/views/delivery/DeliveryEngagementView.vue'),
        meta: { title: 'Engagement' },
      },
      // My Tasks — CRM follow-up tasks assigned to the user (incl. those spawned
      // by Decisions + AI-outreach replies). What client-portal users see.
      {
        path: 'my-tasks',
        name: 'my-tasks',
        component: () => import('@/views/tasks/MyTasksView.vue'),
        meta: { title: 'Your tasks' },
      },
      // LP/CDP Performance Hub — the front door to the entire suite.
      // Workspace-aware landing page with hero KPIs, quick-access cards
      // for each work surface, and a decisions recency feed. What a
      // media client sees first when they log in.
      {
        path: 'lp',
        redirect: { name: 'lp-hub' },
      },
      {
        path: 'lp/hub',
        name: 'lp-hub',
        component: () => import('@/views/lp/LpPerformanceHubView.vue'),
        meta: { title: 'Performance hub' },
      },
      // LeadPerfection performance — tenant-scoped dashboard against synced LP data.
      {
        path: 'lp/performance',
        name: 'lp-performance',
        component: () => import('@/views/lp/LpPerformanceView.vue'),
        meta: { title: 'LP performance' },
      },
      // Customer 360 / Journey — search a customer (phone/email/id) and see
      // their entire interaction history across LP + Mavriq systems with full
      // campaign attribution (first touch / last touch / final step).
      {
        path: 'lp/customer-journey',
        name: 'lp-customer-journey',
        component: () => import('@/views/lp/LpCustomerJourneyView.vue'),
        meta: { title: 'Customer journey' },
      },
      // Vendor export ingestion — bridge campaign attribution into the
      // journey by uploading lead exports from aggregator portals
      // (Truvolv, HeavySet Tech, Angies List, etc.).
      {
        path: 'lp/aggregator-import',
        name: 'lp-aggregator-import',
        component: () => import('@/views/lp/LpAggregatorImportView.vue'),
        meta: { title: 'Import vendor export' },
      },
      // Mavriq Blue-Collar CDP — settings + multi-touch attribution.
      // The CDP layer sits ABOVE the per-system attribution we already
      // expose on LP Performance, and triangulates Google Ads + GA4 +
      // aggregator + LP + CallRail + pixel into a single customer view.
      {
        path: 'lp/cdp',
        name: 'lp-cdp-settings',
        component: () => import('@/views/lp/CdpSettingsView.vue'),
        meta: { title: 'CDP settings' },
      },
      {
        path: 'lp/cdp-attribution',
        name: 'lp-cdp-attribution',
        component: () => import('@/views/lp/CdpAttributionView.vue'),
        meta: { title: 'CDP attribution' },
      },
      // Grand ROI — the cleanest portfolio-level + per-campaign ROAS
      // readout, combining ad spend + CDP multi-touch + LP-verified
      // contracts + confidence grading.
      {
        path: 'lp/grand-roi',
        name: 'lp-grand-roi',
        component: () => import('@/views/lp/GrandRoiView.vue'),
        meta: { title: 'Grand ROI' },
      },
      // Scenario planner — saturation curves per campaign + what-if
      // sliders + AI explanation in operator language ("Keen for
      // owner-operators"). Phase 2a of the MMM-lite layer.
      {
        path: 'lp/scenario-planner',
        name: 'lp-scenario-planner',
        component: () => import('@/views/lp/ScenarioPlannerView.vue'),
        meta: { title: 'Scenario planner' },
      },
      // Annual planner — Phase 2c: seasonality detection + Lagrangian
      // equal-marginal-ROI allocator. Owner inputs an annual budget,
      // gets a month-by-month, channel-by-channel plan + AI explanation.
      {
        path: 'lp/annual-planner',
        name: 'lp-annual-planner',
        component: () => import('@/views/lp/AnnualPlannerView.vue'),
        meta: { title: 'Annual planner' },
      },
      // Competitive — Phase 3a: Pathmatics-class competitor ad
      // intelligence built on free official ad libraries (Meta first).
      {
        path: 'lp/competitive',
        name: 'lp-competitive',
        component: () => import('@/views/lp/CompetitiveView.vue'),
        meta: { title: 'Competitive' },
      },
      // CSR Performance — Phase 3c: set / sit / close rates per CSR
      // from LP's nested setbyname field + product × disposition cross-tab.
      // Lead flow — leads vs appointments set by week × product, why unset
      // leads didn't book, close rate over time (team + per CSR).
      // Spend impact — before/after of a sustained Google spend change vs the
      // same weeks last year, alternative explanations, economics, restore test.
      {
        path: 'lp/spend-impact',
        name: 'lp-spend-impact',
        component: () => import('@/views/lp/SpendImpactView.vue'),
        meta: { title: 'Spend impact' },
      },
      {
        path: 'lp/lead-flow',
        name: 'lp-lead-flow',
        component: () => import('@/views/lp/LeadFlowView.vue'),
        meta: { title: 'Leads vs appointments' },
      },
      {
        path: 'lp/csr-performance',
        name: 'lp-csr-performance',
        component: () => import('@/views/lp/CsrPerformanceView.vue'),
        meta: { title: 'CSR performance' },
      },
      // Attribution Lens — multi-lens comparison (CSR / LP job source /
      // aggregator UTM / CDP 5 models / Google Ads tracked). Greg's
      // 'I want all ways of looking at it' ask 2026-06-23.
      {
        path: 'lp/attribution-lens',
        name: 'lp-attribution-lens',
        component: () => import('@/views/lp/AttributionLensView.vue'),
        meta: { title: 'Attribution lens' },
      },
      // Front desk vs click — the focused two-column reconciliation that
      // puts CSR coding (the marketing-sheet basis) next to the tracked
      // digital touch, sorted by disagreement, with a spend→ROI bridge.
      // Greg 2026-06-23: "this is where they're saying TV is beating digital."
      {
        path: 'lp/attribution-reconcile',
        name: 'lp-attribution-reconcile',
        component: () => import('@/views/lp/CsrVsClickView.vue'),
        meta: { title: 'Front desk vs click' },
      },
      // Performance Diagnosis — full web→ads→leads→sales read, 2025 vs 2026
      // month-by-month, with AI synthesis + interior/exterior + hail decomposition.
      // Greg 2026-06-24: "a full report ... it can't all be hail."
      {
        path: 'lp/diagnosis',
        name: 'lp-performance-diagnosis',
        component: () => import('@/views/lp/PerformanceDiagnosisView.vue'),
        meta: { title: 'Performance diagnosis' },
      },
      // Full Operational Funnel — Phase 3d: lead → call → set → sat →
      // sold → project_start → done, sliced by CSR / sales rep / product
      // with median durations between stages.
      {
        path: 'lp/funnel',
        name: 'lp-funnel',
        component: () => import('@/views/lp/FunnelView.vue'),
        meta: { title: 'Full funnel' },
      },
      // Report Builder lives at /reports/builder (promoted to top-level nav 2026-06-19).
      // Keep a redirect from the old /lp/report-builder path so any bookmarks survive.
      {
        path: 'lp/report-builder',
        redirect: '/reports/builder',
      },
      // Per-lead attribution drill-down with data provenance
      {
        path: 'lp/lead-attribution',
        name: 'lp-lead-attribution',
        component: () => import('@/views/lp/LeadAttributionView.vue'),
        meta: { title: 'Lead attribution' },
      },
      // Forward-look booked calendar (schedule capacity)
      {
        path: 'lp/booked-calendar',
        name: 'lp-booked-calendar',
        component: () => import('@/views/lp/BookedCalendarView.vue'),
        meta: { title: 'Booked calendar' },
      },
      // Weekly funnel snapshot + cohort attribution
      {
        path: 'lp/funnel-report',
        name: 'lp-funnel-report',
        component: () => import('@/views/lp/FunnelReportView.vue'),
        meta: { title: 'Funnel report' },
      },
      // Weekly Brief — auto-drafted client report (Mavriq voice)
      {
        path: 'lp/weekly-brief',
        name: 'lp-weekly-brief',
        component: () => import('@/views/lp/WeeklyBriefView.vue'),
        meta: { title: 'Weekly Brief' },
      },
      // Connect — data sources + sync health (reuses the connectors screen).
      {
        path: 'connect',
        name: 'connect',
        component: () => import('@/views/configure/ConnectorsView.vue'),
        meta: { title: 'Connect' },
      },

      // Mavriq Intelligence Platform — exec briefing, insights feed,
      // growth stress test, knowledge interview + browser.
      {
        path: 'today',
        name: 'mip-briefing',
        component: () => import('@/views/today/TodayView.vue'),
        meta: { title: 'Today' },
      },
      {
        path: 'welcome',
        name: 'mip-onboarding',
        component: () => import('@/views/intelligence/MipOnboardingView.vue'),
        meta: { title: 'Welcome' },
      },
      {
        path: 'insights',
        name: 'mip-insights',
        component: () => import('@/views/intelligence/MipInsightsFeedView.vue'),
        meta: { title: 'Insights' },
      },
      {
        path: 'stress-test',
        name: 'mip-growth-test',
        component: () => import('@/views/intelligence/MipGrowthTestView.vue'),
        meta: { title: 'If you had to grow…' },
      },
      {
        path: 'playbooks',
        name: 'mip-knowledge',
        component: () => import('@/views/intelligence/MipKnowledgeView.vue'),
        meta: { title: 'Playbooks & knowledge' },
      },
      {
        path: 'playbooks/interview',
        name: 'mip-interview',
        component: () => import('@/views/intelligence/MipKnowledgeInterviewView.vue'),
        meta: { title: 'Tell us how you run' },
      },
      {
        path: 'platform/health',
        name: 'mip-admin-health',
        component: () => import('@/views/intelligence/MipAdminHealthView.vue'),
        meta: { title: 'Platform health', requiresRole: ['admin', 'partner'] },
      },
      {
        path: 'platform/cost-per-client',
        name: 'mip-cost-per-client',
        component: () => import('@/views/intelligence/MipCostPerClientView.vue'),
        meta: { title: 'Cost per client', requiresRole: ['admin', 'partner'] },
      },
      {
        path: 'notifications/settings',
        name: 'mip-notifications-settings',
        component: () => import('@/views/intelligence/MipNotificationsSettingsView.vue'),
        meta: { title: 'Alert preferences' },
      },
      {
        path: 'webhooks',
        name: 'mip-webhooks',
        component: () => import('@/views/intelligence/MipWebhooksView.vue'),
        meta: { title: 'Webhooks' },
      },

      // Email Sequences
      {
        path: 'sequences',
        name: 'sequences',
        component: () => import('@/views/intelligence/SequencesView.vue'),
        meta: { title: 'Email Sequences' },
      },

      // Sales Targets
      {
        path: 'sales-targets',
        name: 'sales-targets',
        component: () => import('@/views/sales-targets/SalesTargetsView.vue'),
        meta: { title: 'Sales Targets' },
      },
      {
        path: 'sales-targets/dashboard',
        name: 'sales-targets-dashboard',
        component: () => import('@/views/sales-targets/SalesTargetsDashboardView.vue'),
        meta: { title: 'Sales Targets Dashboard' },
      },
      {
        path: 'sales-targets/import',
        name: 'sales-targets-import',
        component: () => import('@/views/sales-targets/SalesTargetsImportView.vue'),
        meta: { title: 'Import Sales Targets' },
      },

      // Analytics (Google Analytics) - UA-style layout with sidebar
      {
        path: 'analytics',
        component: () => import('@/views/analytics/AnalyticsLayoutView.vue'),
        meta: { title: 'Analytics' },
        children: [
          {
            path: '',
            name: 'analytics',
            component: () => import('@/views/analytics/AnalyticsDashboardView.vue'),
            meta: { title: 'Analytics' },
          },
          {
            path: 'pages/:pagePath',
            name: 'analytics-page-detail',
            component: () => import('@/views/analytics/AnalyticsDetailView.vue'),
            props: { type: 'page' },
            meta: { title: 'Page Analytics' },
          },
          {
            path: 'sources/:sourceMedium',
            name: 'analytics-source-detail',
            component: () => import('@/views/analytics/AnalyticsDetailView.vue'),
            props: { type: 'source' },
            meta: { title: 'Source Analytics' },
          },
          {
            path: 'campaigns/:campaignName',
            name: 'analytics-campaign-detail',
            component: () => import('@/views/analytics/AnalyticsDetailView.vue'),
            props: { type: 'campaign' },
            meta: { title: 'Campaign Analytics' },
          },
        ],
      },

      // Reports
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/reports/ReportsView.vue'),
        meta: { title: 'Reports' },
      },
      {
        path: 'reports/clients',
        name: 'reports-clients-overview',
        component: () => import('@/views/reports/ClientsOverviewView.vue'),
        meta: { title: 'Clients Overview', roles: ['admin', 'partner', 'senior_consultant'] },
      },
      {
        path: 'reports/client',
        name: 'client-reports',
        component: () => import('@/views/reports/ClientReportView.vue'),
        meta: { title: 'Client Reports', roles: ['admin', 'partner', 'senior_consultant'] },
      },
      {
        path: 'reports/client-dashboard',
        name: 'client-dashboard',
        component: () => import('@/views/reports/UnifiedClientDashboardView.vue'),
        meta: { title: 'Client Dashboard' },
      },
      {
        path: 'reports/weekly',
        name: 'weekly-reports',
        component: () => import('@/views/reports/WeeklyReportsQueueView.vue'),
        meta: { title: 'Weekly Reports', roles: ['admin', 'partner'] },
      },
      {
        path: 'reports/weekly/:snapshotId',
        name: 'weekly-report-detail',
        component: () => import('@/views/reports/WeeklyReportDetailView.vue'),
        meta: { title: 'Weekly Report', roles: ['admin', 'partner'] },
      },
      {
        path: 'reports/builder',
        name: 'report-builder',
        component: () => import('@/views/reports/ReportBuilderView.vue'),
        meta: { title: 'Report Builder' },
      },
      {
        path: 'reports/builder/canvas',
        name: 'report-builder-canvas',
        component: () => import('@/views/reports/ReportBuilderCanvasView.vue'),
        // requiresCanvasBuilder: gated at navigation guard time. Mavriq team always
        // passes; client portal users only see it when their admin enables the flag.
        meta: { title: 'Report Builder · Canvas', requiresCanvasBuilder: true },
      },
      {
        path: 'reports/templates',
        name: 'report-templates',
        component: () => import('@/views/reports/ReportTemplatesView.vue'),
        meta: { title: 'Report Templates' },
      },
      {
        path: 'reports/performance-insights',
        name: 'performance-insights',
        component: () => import('@/views/reports/PerformanceInsightsView.vue'),
        meta: { title: 'Performance Insights' },
      },

      // SMS Messaging
      {
        path: 'messaging',
        name: 'messaging',
        component: () => import('@/views/messaging/MessagingView.vue'),
        meta: { title: 'SMS Messages' },
      },

      // Email Campaigns
      {
        path: 'email-campaigns',
        name: 'email-campaigns',
        component: () => import('@/views/email-campaigns/EmailCampaignsView.vue'),
        meta: { title: 'Email Campaigns' },
      },
      {
        path: 'email-campaigns/new',
        name: 'email-campaign-new',
        component: () => import('@/views/email-campaigns/CampaignEditView.vue'),
        meta: { title: 'New Campaign' },
      },
      {
        path: 'email-campaigns/:id',
        name: 'email-campaign-detail',
        component: () => import('@/views/email-campaigns/CampaignDetailView.vue'),
        meta: { title: 'Campaign Details' },
      },
      {
        path: 'email-campaigns/:id/edit',
        name: 'email-campaign-edit',
        component: () => import('@/views/email-campaigns/CampaignEditView.vue'),
        meta: { title: 'Edit Campaign' },
      },
      {
        path: 'email-campaigns/templates',
        name: 'email-templates',
        component: () => import('@/views/email-campaigns/TemplatesView.vue'),
        meta: { title: 'Email Templates' },
      },
      {
        path: 'email-campaigns/templates/:id/edit',
        name: 'email-template-edit',
        component: () => import('@/views/email-campaigns/TemplateEditView.vue'),
        meta: { title: 'Edit Template' },
      },
      {
        path: 'email-campaigns/lists',
        name: 'email-lists',
        component: () => import('@/views/email-campaigns/ListsView.vue'),
        meta: { title: 'Email Lists' },
      },
      {
        path: 'email-campaigns/analytics',
        name: 'email-analytics',
        component: () => import('@/views/email-campaigns/AnalyticsView.vue'),
        meta: { title: 'Email Analytics' },
      },
      {
        path: 'email-campaigns/deliverability',
        name: 'email-deliverability',
        component: () => import('@/views/email-campaigns/DeliverabilityView.vue'),
        meta: { title: 'Sender authentication' },
      },
      {
        path: 'email-campaigns/ab-testing',
        name: 'email-ab-testing',
        component: () => import('@/views/email-campaigns/ABTestingView.vue'),
        meta: { title: 'A/B Testing' },
      },
      {
        path: 'email-campaigns/automations',
        name: 'email-automations',
        component: () => import('@/views/email-campaigns/AutomationsView.vue'),
        meta: { title: 'Email Automations' },
      },
      {
        path: 'email-campaigns/automations/:id/edit',
        name: 'email-automation-edit',
        component: () => import('@/views/email-campaigns/AutomationEditView.vue'),
        meta: { title: 'Edit Automation' },
      },

      // Referrals
      {
        path: 'referrals',
        name: 'referrals',
        component: () => import('@/views/referrals/ReferralsView.vue'),
        meta: { title: 'Referral Program' },
      },
      {
        path: 'referrals/settings',
        name: 'referral-settings',
        component: () => import('@/views/referrals/ReferralSettingsView.vue'),
        meta: { title: 'Referral Settings' },
      },

      // Forms
      {
        path: 'forms',
        name: 'forms',
        component: () => import('@/views/forms/FormsView.vue'),
        meta: { title: 'Forms' },
      },
      {
        path: 'forms/:id/edit',
        name: 'form-edit',
        component: () => import('@/views/forms/FormEditView.vue'),
        meta: { title: 'Edit Form' },
      },
      {
        path: 'forms/:id/preview',
        name: 'form-preview',
        component: () => import('@/views/forms/FormPreviewView.vue'),
        meta: { title: 'Preview Form' },
      },
      {
        path: 'forms/:id/submissions',
        name: 'form-submissions',
        component: () => import('@/views/forms/FormSubmissionsView.vue'),
        meta: { title: 'Form Submissions' },
      },

      // Configure (New Unified Settings)
      {
        path: 'configure',
        name: 'configure',
        component: () => import('@/views/configure/ConfigureView.vue'),
        meta: { title: 'Configure' },
        children: [
          {
            path: '',
            redirect: '/configure/profile',
          },
          {
            path: 'profile',
            name: 'configure-profile',
            component: () => import('@/views/settings/ProfileSettings.vue'),
            meta: { title: 'Profile' },
          },
          {
            path: 'notifications',
            name: 'configure-notifications',
            component: () => import('@/views/settings/ProfileSettings.vue'), // Reuse for now
            meta: { title: 'Notifications' },
          },
          {
            path: 'pipeline',
            name: 'configure-pipeline',
            component: () => import('@/views/settings/PipelineSettings.vue'),
            meta: { title: 'Pipeline', roles: ['admin', 'partner', 'manager'] },
          },
          {
            path: 'planner-bridge',
            name: 'configure-planner-bridge',
            component: () => import('@/views/settings/PlannerBridgeSettings.vue'),
            meta: { title: 'Dynamics ↔ Planner Bridge' },
          },
          {
            path: 'getting-started',
            name: 'configure-getting-started',
            component: () => import('@/views/settings/OnboardingChecklist.vue'),
            meta: { title: 'Getting Started' },
          },
          {
            path: 'custom-fields',
            name: 'configure-custom-fields',
            component: () => import('@/views/settings/CustomFieldsSettings.vue'),
            meta: { title: 'Custom Fields', roles: ['admin', 'partner'] },
          },
          {
            path: 'lead-scoring',
            name: 'configure-lead-scoring',
            component: () => import('@/views/settings/LeadScoringSettings.vue'),
            meta: { title: 'Lead Scoring', roles: ['admin', 'partner', 'manager'] },
          },
          {
            path: 'automations',
            name: 'configure-automations',
            component: () => import('@/views/settings/AutomationsSettings.vue'),
            meta: { title: 'Automations', roles: ['admin', 'partner', 'manager'] },
          },
          {
            path: 'email',
            name: 'configure-email',
            component: () => import('@/views/settings/IntegrationSettings.vue'), // Reuse for now
            meta: { title: 'Email Settings', roles: ['admin', 'partner', 'manager'] },
          },
          {
            path: 'social',
            name: 'configure-social',
            component: () => import('@/views/settings/IntegrationSettings.vue'), // Reuse for now
            meta: { title: 'Social Accounts', roles: ['admin', 'partner', 'manager'] },
          },
          {
            path: 'integrations',
            name: 'configure-integrations',
            component: () => import('@/views/configure/ConnectorsView.vue'),
            meta: { title: 'Integrations' },
          },
          {
            path: 'enrichment',
            name: 'configure-enrichment',
            component: () => import('@/views/settings/EnrichmentSettings.vue'),
            meta: { title: 'Data Enrichment', roles: ['admin', 'partner', 'manager'] },
          },
          {
            path: 'api-config',
            name: 'configure-api-config',
            component: () => import('@/views/settings/ApiConfigSettings.vue'),
            meta: { title: 'API Keys', roles: ['admin', 'partner'] },
          },
          {
            path: 'document-templates',
            name: 'configure-document-templates',
            component: () => import('@/views/settings/DocumentTemplatesSettings.vue'),
            meta: { title: 'Document Templates', roles: ['admin', 'partner'] },
          },
          {
            path: 'dynamics',
            name: 'configure-dynamics',
            component: () => import('@/views/configure/DynamicsSettingsView.vue'),
            meta: { title: 'Dynamics 365', roles: ['admin', 'partner'] },
          },
          {
            path: 'team',
            name: 'configure-team',
            component: () => import('@/views/settings/TeamSettings.vue'),
            meta: { title: 'Team Members', roles: ['admin', 'partner', 'manager'] },
          },
          {
            path: 'roles',
            name: 'configure-roles',
            component: () => import('@/views/settings/TeamSettings.vue'), // Reuse for now
            meta: { title: 'Roles & Permissions', roles: ['admin', 'partner'] },
          },
          {
            path: 'import',
            name: 'configure-import',
            component: () => import('@/views/settings/ImportDataView.vue'),
            meta: { title: 'Import Data', roles: ['admin', 'partner', 'senior_consultant'] },
          },
          {
            path: 'export',
            name: 'configure-export',
            component: () => import('@/views/settings/ImportDataView.vue'), // Reuse for now
            meta: { title: 'Export Data', roles: ['admin', 'partner'] },
          },
          {
            path: 'intake',
            name: 'configure-intake',
            component: () => import('@/views/configure/IntakeConfigView.vue'),
            meta: { title: 'Lead Intake' },
          },
          {
            path: 'intake/queue',
            name: 'configure-intake-queue',
            component: () => import('@/views/configure/IntakeQueueView.vue'),
            meta: { title: 'Intake Queue' },
          },
          {
            path: 'kpi-thresholds',
            name: 'configure-kpi-thresholds',
            component: () => import('@/views/configure/KpiThresholdsView.vue'),
            meta: { title: 'KPI Thresholds', roles: ['admin', 'partner', 'senior_consultant'] },
          },
        ],
      },

      // Settings (Legacy - redirect to configure)
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/settings/SettingsView.vue'),
        meta: { title: 'Settings' },
        children: [
          {
            path: '',
            name: 'settings-profile',
            component: () => import('@/views/settings/ProfileSettings.vue'),
            meta: { title: 'Profile Settings' },
          },
          {
            path: 'team',
            name: 'settings-team',
            component: () => import('@/views/settings/TeamSettings.vue'),
            meta: { title: 'Team Settings', roles: ['admin', 'manager'] },
          },
          {
            path: 'pipeline',
            name: 'settings-pipeline',
            component: () => import('@/views/settings/PipelineSettings.vue'),
            meta: { title: 'Pipeline Settings', roles: ['admin', 'manager'] },
          },
          {
            path: 'custom-fields',
            name: 'settings-custom-fields',
            component: () => import('@/views/settings/CustomFieldsSettings.vue'),
            meta: { title: 'Custom Fields', roles: ['admin', 'partner'] },
          },
          {
            path: 'automations',
            name: 'settings-automations',
            component: () => import('@/views/settings/AutomationsSettings.vue'),
            meta: { title: 'Automations', roles: ['admin', 'manager'] },
          },
          {
            path: 'lead-scoring',
            name: 'settings-lead-scoring',
            component: () => import('@/views/settings/LeadScoringSettings.vue'),
            meta: { title: 'Lead Scoring', roles: ['admin', 'manager'] },
          },
          {
            path: 'integrations',
            name: 'settings-integrations',
            component: () => import('@/views/configure/ConnectorsView.vue'),
            meta: { title: 'Integrations' },
          },
          {
            path: 'enrichment',
            name: 'settings-enrichment',
            component: () => import('@/views/settings/EnrichmentSettings.vue'),
            meta: { title: 'Data Enrichment', roles: ['admin', 'manager'] },
          },
          {
            path: 'api-config',
            name: 'settings-api-config',
            component: () => import('@/views/settings/ApiConfigSettings.vue'),
            meta: { title: 'API Configuration', roles: ['admin', 'partner'] },
          },
          {
            path: 'document-templates',
            name: 'settings-document-templates',
            component: () => import('@/views/settings/DocumentTemplatesSettings.vue'),
            meta: { title: 'Document Templates', roles: ['admin', 'partner'] },
          },
          {
            path: 'import',
            name: 'settings-import',
            component: () => import('@/views/settings/ImportDataView.vue'),
            meta: { title: 'Import Data', roles: ['admin', 'partner', 'senior_consultant'] },
          },
        ],
      },

      // Admin routes
      {
        path: 'admin',
        name: 'admin',
        component: () => import('@/views/admin/AdminDashboardView.vue'),
        meta: { title: 'Admin Dashboard', roles: ['admin', 'partner'] },
      },
      {
        path: 'admin/client-users',
        name: 'admin-client-users',
        component: () => import('@/views/admin/ClientUsersView.vue'),
        meta: { title: 'Client Users', roles: ['admin', 'partner'] },
      },
      {
        path: 'admin/companies',
        name: 'admin-companies',
        component: () => import('@/views/admin/AdminCompaniesView.vue'),
        meta: { title: 'Company Management', roles: ['admin', 'partner'] },
      },
      {
        path: 'admin/audit-log',
        name: 'admin-audit-log',
        component: () => import('@/views/admin/AuditLogView.vue'),
        meta: { title: 'Audit Log', roles: ['admin', 'partner'] },
      },
      {
        path: 'admin/settings',
        name: 'admin-settings',
        component: () => import('@/views/admin/AdminSettingsView.vue'),
        meta: { title: 'System Settings', roles: ['admin'] },
      },
      {
        path: 'admin/branding',
        name: 'admin-branding',
        component: () => import('@/views/admin/BrandingSettingsView.vue'),
        meta: { title: 'Branding Settings', roles: ['admin', 'partner'] },
      },
      {
        path: 'admin/multi-tenant',
        name: 'admin-multi-tenant',
        component: () => import('@/views/admin/MultiTenantDashboardView.vue'),
        meta: { title: 'Multi-Tenant Management', roles: ['admin'] },
      },
      {
        path: 'admin/feedback',
        name: 'admin-feedback',
        component: () => import('@/views/admin/FeedbackReviewView.vue'),
        meta: { title: 'Feedback & Errors', roles: ['admin', 'partner'] },
      },
    ],
  },

  // Public 50% Question brief (no auth). Addressed only by short code —
  // deliberately outside the app chrome so it reads as a briefing, not an app.
  // Staff preview of a draft, using the same component so what you review is
  // literally the page the prospect gets. Must be declared BEFORE /brief/:code
  // or "preview" would be matched as a short code.
  {
    path: '/brief/preview/:previewId',
    name: 'brief-preview',
    component: () => import('@/views/public/PublicBriefView.vue'),
    meta: { requiresAuth: true, title: 'Brief preview' },
  },
  {
    path: '/brief/:code',
    name: 'public-brief',
    component: () => import('@/views/public/PublicBriefView.vue'),
    meta: { title: 'The 50% Question' },
  },

  // Public Document Viewer (no auth required)
  {
    path: '/view/:shortCode',
    name: 'document-viewer',
    component: () => import('@/views/public/DocumentViewerView.vue'),
    meta: { title: 'View Document' },
  },

  // Public space booking (no auth) — guests / day-pass. :location = tenant id.
  {
    path: '/book/:location',
    name: 'public-book-space',
    component: () => import('@/views/public/PublicBookView.vue'),
    meta: { title: 'Book a Space' },
  },

  // Public CMS site (no auth). :location = tenant id.
  {
    path: '/site/:location',
    name: 'public-site',
    component: () => import('@/views/public/PublicSiteView.vue'),
    meta: { title: 'Site' },
  },
  {
    path: '/site/:location/:slug',
    name: 'public-site-page',
    component: () => import('@/views/public/PublicSiteView.vue'),
    meta: { title: 'Site' },
  },

  // Coworking member portal (self-serve, Firebase-authed inside the views).
  {
    path: '/member/:location/login',
    name: 'member-login',
    component: () => import('@/views/member/MemberAuthView.vue'),
    meta: { title: 'Member Login' },
  },
  {
    path: '/member/:location/signup',
    name: 'member-signup',
    component: () => import('@/views/member/MemberAuthView.vue'),
    meta: { title: 'Create Account' },
  },
  {
    path: '/member/:location',
    name: 'member-home',
    component: () => import('@/views/member/MemberHomeView.vue'),
    meta: { title: 'My Account' },
  },

  // Legal Pages (public, no auth required)
  {
    path: '/legal/terms',
    name: 'terms-of-service',
    component: () => import('@/views/legal/TermsView.vue'),
    meta: { title: 'Terms of Service' },
  },
  {
    path: '/legal/privacy',
    name: 'privacy-policy',
    component: () => import('@/views/legal/PrivacyView.vue'),
    meta: { title: 'Privacy Policy' },
  },

  // 404 - Not Found
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page Not Found' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Global navigation guards
router.beforeEach(authGuard)
router.afterEach(setPageTitle)

// Stale-chunk recovery. After a new deploy, hashed lazy chunks the running
// bundle references are replaced on the server, so a route's dynamic import
// 404s ("Failed to fetch dynamically imported module"). Force a one-time reload
// to the fresh index.html so non-technical users never have to hard-refresh.
router.onError((error, to) => {
  const msg = String((error as Error)?.message || '')
  const isStaleChunk = /dynamically imported module|Importing a module script failed|Failed to fetch|error loading dynamically imported/i.test(msg)
  if (isStaleChunk && to?.fullPath) {
    const KEY = 'stale-chunk-reload'
    // Guard against a reload loop if the chunk is genuinely missing.
    if (sessionStorage.getItem(KEY) !== to.fullPath) {
      sessionStorage.setItem(KEY, to.fullPath)
      window.location.assign(to.fullPath)
    }
  }
})
// Clear the reload guard once a navigation succeeds, so the next deploy can
// recover again.
router.afterEach(() => {
  try { sessionStorage.removeItem('stale-chunk-reload') } catch { /* ignore */ }
})

export default router
